import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Button } from "../../../../components/ui/button";
import { Separator } from "../../../../components/ui/separator";
import { TbFileExport } from "react-icons/tb";
import { patientBasicInfoType } from "../../Profile/PatientTypes";
import { z } from "zod";
import { PatientMedicalInfoSchema } from "../../MedicalProfile/PatientMedicalProfileForm";
import { Skeleton } from "../../../../components/ui/skeleton";
import dayjs from "dayjs";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  loading = false,
  patientBasicInfoState,
  patientMedicalInfoState,
}: DataTableProps<TData, TValue> & {
  patientBasicInfoState: patientBasicInfoType;
} & { patientMedicalInfoState: z.infer<typeof PatientMedicalInfoSchema> }) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  const exportToPDF = () => {
    const doc = new jsPDF();

    // Extract Headers
    console.log(table.getRowModel());
    const headers = table
      .getHeaderGroups()[0]
      .headers.filter((header) => header.id !== "actions")
      .map((header) => {
        return header.column.columnDef.header;
      });

    // Extract Rows
    const rows = table.getRowModel().rows.map((row) =>
      row
        .getVisibleCells()
        .filter((cell) => cell.getValue() !== undefined)
        .map((cell) => cell.getValue())
    );
    let x = 15;
    let y = 50;
    let printList = [];

    doc.setFontSize(12);
    doc.text(`Date: ${dayjs().format("DD-MM-YYYY")}`, 150, 40);
    if (
      patientBasicInfoState.firstName !== undefined &&
      patientBasicInfoState.lastName !== undefined
    ) {
      printList.push(
        `Patient Name: ${patientBasicInfoState.firstName} ${patientBasicInfoState.lastName}`
      );
    }
    if (patientMedicalInfoState.dateOfBirth !== undefined) {
      printList.push(
        `Patient date-of-birth: ${patientMedicalInfoState.dateOfBirth.format(
          "DD-MM-YYYY"
        )}`
      );
    }
    if (patientMedicalInfoState.gender !== undefined) {
      printList.push(`Patient gender: ${patientMedicalInfoState.gender}`);
    }
    doc.setFontSize(12);
    doc.text(printList, 14, y);

    // Generate PDF Table
    doc.autoTable({
      head: [headers],
      body: rows,
      margin: { top: 70 },
    });

    // Generate pdf as blob
    const pdfBlob = doc.output("blob");

    // Create a temporary URL

    const blobUrl = URL.createObjectURL(pdfBlob);
    // Save PDF
    // doc.save("prescriptions.pdf");

    // Open the PDF in a new tab

    window.open(blobUrl, "_blank");
  };
  return (
    <div className="">
      <Table className="bg-slate-200 rounded-lg">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id} className="font-bold text-black">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 3 }).map((_, r) => (
              <TableRow key={`skeleton-${r}`}>
                {columns.map((_, c) => (
                  <TableCell key={`skeleton-${r}-${c}`}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className=" text-black">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                No prescriptions yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Button className="my-4 bg-green-700 " onClick={exportToPDF}>
        <TbFileExport />
        Export to PDF
      </Button>
      <Separator />
    </div>
  );
}
